import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

export const AuthModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1200;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 48px 16px 20px;
	overflow-y: auto;
	background: var(--bt-auth-overlay-bg, rgba(10, 14, 26, 0.94));
	backdrop-filter: blur(12.5px);
	animation: ${fadeIn} 0.3s ease-in forwards;

	@media (min-width: 1024px) {
		align-items: center;
		padding: 20px 16px;
	}

	@media (max-width: 767px) {
		padding: 16px 12px;
		align-items: flex-start;
	}
`;

export const AuthModalCard = styled.div`
	position: relative;
	width: 100%;
	max-width: ${({ $wide }) => ($wide ? "920px" : "470px")};
	max-height: calc(100vh - 48px);
	background: var(--bt-auth-modal-bg, #171c2a);
	border: 1px solid var(--bt-auth-modal-border, #212737);
	border-radius: ${({ $wide }) => ($wide ? "28px" : "24px")};
	overflow: hidden;
	display: flex;
	flex-direction: ${({ $wide }) => ($wide ? "row" : "column")};
	align-items: ${({ $wide }) => ($wide ? "stretch" : "stretch")};

	${({ $wide }) =>
		$wide &&
		css`
			@media (min-width: 1024px) {
				min-height: 532px;
			}
		`}

	@media (max-width: 1023px) {
		flex-direction: column;
		max-width: calc(100vw - 24px);
		max-height: calc(100vh - 32px);
		border-radius: 20px;
	}
`;

export const AuthCloseButton = styled.button`
	position: absolute;
	top: ${({ $wide }) => ($wide ? "19px" : "15px")};
	right: ${({ $wide }) => ($wide ? "19px" : "15px")};
	z-index: 3;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ $wide }) => ($wide ? "34px" : "32px")};
	height: ${({ $wide }) => ($wide ? "34px" : "32px")};
	padding: 8px;
	border: none;
	border-radius: ${({ $wide }) => ($wide ? "10px" : "8px")};
	background: var(--bt-auth-close-bg, #2b3245);
	color: var(--bt-auth-heading-color, #e5f0fe);
	cursor: pointer;

	svg {
		width: 16px;
		height: 16px;
	}
`;

export const AuthPromoPanel = styled.div`
	display: none;
	overflow: hidden;
	background: #0d1220;
	min-height: 0;

	--auth-promo-desktop-width: 400px;
	--auth-promo-desktop-height: 500px;
	--auth-promo-compact-width: 500px;
	--auth-promo-compact-height: 200px;

	@media (min-width: 1024px) {
		display: block;
		flex: 0 0 auto;
		width: min(var(--auth-promo-desktop-width), 100%);
		max-width: var(--auth-promo-desktop-width);
		aspect-ratio: 400 / 500;
		height: auto;
		max-height: var(--auth-promo-desktop-height);
		align-self: center;
		margin: 16px 0 16px 16px;
		border-radius: 18px;
	}

	img,
	picture {
		width: 100%;
		height: 100%;
		display: block;
	}

	img {
		object-fit: cover;
		object-position: center center;
	}

	@media (max-width: 1023px) {
		display: block;
		flex: none;
		width: min(var(--auth-promo-compact-width), calc(100% - 32px));
		height: auto;
		aspect-ratio: 500 / 200;
		max-height: var(--auth-promo-compact-height);
		margin: 16px auto 0;
		border-radius: 14px;
	}
`;

export const AuthModalBody = styled.div`
	flex: ${({ $constrained }) => ($constrained ? "0 0 374px" : "1 1 auto")};
	display: flex;
	flex-direction: column;
	width: ${({ $constrained }) => ($constrained ? "374px" : "100%")};
	max-width: ${({ $constrained }) => ($constrained ? "374px" : "100%")};
	padding: ${({ $wide, $constrained }) =>
		$constrained ? "40px 24px 28px 0" : $wide ? "32px 28px 28px" : "40px 48px 32px"};
	min-width: 0;
	overflow-y: auto;
	${({ $constrained }) =>
		$constrained &&
		css`
			@media (min-width: 1024px) {
				margin-left: 20px;
			}
		`}

	@media (max-width: 1023px) {
		flex: 1 1 auto;
		width: 100%;
		max-width: none;
		margin-left: 0;
		padding: 16px 20px 20px;
	}

	@media (max-width: 767px) {
		padding: 14px 16px 18px;
	}
`;

export const AuthFormShell = styled.div`
	width: 100%;
	max-width: 374px;
	margin: 0 auto;

	.ant-form-item textarea.ant-input {
		border: none !important;
		background: var(--bt-auth-input-bg, #1f2536) !important;
		border-radius: 10px !important;
		resize: none;
	}

	.ant-form-item textarea.ant-input:focus {
		border: 1px solid var(--bt-auth-primary, #0362d4) !important;
		box-shadow: none !important;
	}

	.ant-input-affix-wrapper-focused {
		border: 1px solid var(--bt-auth-primary, #0362d4) !important;
		box-shadow: none !important;
		overflow: visible !important;
	}

	.ant-form-item-control-input {
		min-height: auto !important;
		overflow: visible;
	}

	.ant-form-item-control-input input:focus,
	.ant-form-item-control-input .ant-input:focus {
		box-shadow: none !important;
	}

	.ant-input-affix-wrapper .ant-input:focus {
		border: none !important;
		background: transparent !important;
		box-shadow: none !important;
		height: auto !important;
		min-height: 0 !important;
		line-height: 1.4 !important;
	}

	.ant-form-item .ant-input-affix-wrapper {
		height: 48px !important;
		min-height: 48px !important;
		line-height: normal !important;
	}

	.ant-form-item-control-input-content > div > .ant-input {
		height: 48px !important;
		min-height: 48px !important;
		line-height: normal !important;
	}

	.ant-form-item-control-input-content .ant-input-affix-wrapper .ant-input,
	.ant-form-item-control-input-content .ant-input-affix-wrapper > input.ant-input {
		height: auto !important;
		min-height: 0 !important;
		line-height: 1.4 !important;
		background: transparent !important;
		border: none !important;
		border-radius: 0 !important;
		box-shadow: none !important;
	}

	.ant-select.ant-select-single .ant-select-selector {
		border: none !important;
		height: 48px !important;
		min-height: 48px !important;
		padding: 0 36px 0 10px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-select.ant-select-single .ant-select-selector .ant-select-selection-search-input {
		height: 100% !important;
		line-height: 1.4 !important;
	}

	.ant-select.ant-select-single .ant-select-selector .ant-select-selection-item,
	.ant-select.ant-select-single .ant-select-selector .ant-select-selection-placeholder {
		display: flex !important;
		align-items: center !important;
		line-height: 1.4 !important;
		height: auto !important;
	}

	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	input:-webkit-autofill:active,
	.ant-input:-webkit-autofill,
	.ant-input:-webkit-autofill:hover,
	.ant-input:-webkit-autofill:focus,
	.ant-input:-webkit-autofill:active,
	.ant-input-affix-wrapper > input.ant-input:-webkit-autofill,
	.ant-input-affix-wrapper > input.ant-input:-webkit-autofill:hover,
	.ant-input-affix-wrapper > input.ant-input:-webkit-autofill:focus,
	.ant-input-affix-wrapper > input.ant-input:-webkit-autofill:active {
		-webkit-box-shadow: 0 0 0 1000px var(--bt-auth-input-bg, #1f2536) inset !important;
		box-shadow: 0 0 0 1000px var(--bt-auth-input-bg, #1f2536) inset !important;
		-webkit-text-fill-color: var(--bt-auth-heading-color, #e5f0fe) !important;
		caret-color: var(--bt-auth-heading-color, #e5f0fe);
		background-color: var(--bt-auth-input-bg, #1f2536) !important;
		border: none !important;
		transition:
			background-color 5000s ease-in-out 0s,
			color 5000s ease-in-out 0s;
	}

	.ant-input-affix-wrapper:has(input:-webkit-autofill),
	.ant-input-affix-wrapper:has(input:-webkit-autofill:hover),
	.ant-input-affix-wrapper:has(input:-webkit-autofill:focus),
	.ant-input-affix-wrapper:has(input:-webkit-autofill:active) {
		background: var(--bt-auth-input-bg, #1f2536) !important;
	}

	@media (max-width: 1023px) {
		max-width: none;
		margin: 0;
	}

	@media (max-width: 767px) {
		max-width: none;
	}
`;

export const AuthHeaderIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: 10px;
	background: linear-gradient(180deg, #59a4ff 0%, #388cf0 36.95%, #1060c1 100%);
	margin-bottom: 12px;
	${({ $centered }) =>
		$centered &&
		css`
			margin-left: auto;
			margin-right: auto;
		`}

	svg {
		width: 20px;
		height: 20px;
	}
`;

export const AuthHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 14px;
	text-align: ${({ $centered }) => ($centered ? "center" : "left")};
	align-items: ${({ $centered }) => ($centered ? "center" : "flex-start")};
`;

export const AuthTitle = styled.h2`
	margin: 0;
	color: var(--bt-auth-heading-color, #e5f0fe);
	line-height: 1.2;
	text-align: center;
	font-size: 32px;
	font-weight: 400;

	@media (max-width: 767px) {
		font-size: 24px;
		letter-spacing: -0.72px;
	}
`;

export const AuthSubtitle = styled.p`
	margin: 0;
	color: var(--bt-auth-paragraph-color, #a5b1c1);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
`;

export const AuthTabSwitcher = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 3px;
	border-radius: 14px;
	border: 0.6px solid #2b3245;
	background: #212737;
	margin: 0 auto 32px;
	width: max-content;
`;

export const AuthTabButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 92px;
	padding: 7px 18px;
	border: none;
	border-radius: ${({ $active }) => ($active ? "9px" : "7px")};
	background: ${({ $active }) => ($active ? "var(--bt-auth-primary, #0362d4)" : "transparent")};
	color: ${({ $active }) => ($active ? "#E5F0FE" : "#A5B1C1")};
	font-family: var(--bt-font-family, inherit);
	font-size: 16px;
	font-weight: ${({ $active }) => ($active ? 600 : 400)};
	line-height: 1.4;
	cursor: pointer;
`;

export const AuthForm = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;

	.ant-form-item {
		margin-bottom: 0;
		width: 100%;
	}

	.ant-form-item-explain {
		margin: 6px 0 0;
		font-size: 12px;
	}
`;

export const AuthFieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;
	padding: 1px 1px;
	box-sizing: border-box;
	overflow: visible;
`;

export const AuthFieldLabel = styled.span`
	color: var(--bt-auth-heading-color, #e5f0fe);
	font-family: var(--bt-font-family, inherit);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
`;

export const AuthFieldHelper = styled.span`
	color: var(--bt-auth-paragraph-color, #5b6379);
	font-family: var(--bt-font-family, inherit);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
`;

export const AuthForgotLink = styled.button`
	align-self: flex-end;
	border: none;
	background: transparent;
	padding: 0;
	color: var(--bt-auth-paragraph-color, #a5b1c1);
	font-family: var(--bt-font-family, inherit);
	font-size: 12px;
	font-weight: 600;
	line-height: 1.4;
	letter-spacing: -0.36px;
	cursor: pointer;
`;

export const AuthPrimaryButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 10px 20px;
	height: 46px;
	border: none;
	border-radius: 9px;
	background: #0362d4;
	color: #e5f0fe;
	font-size: 16px;
	font-weight: 400;
	cursor: pointer;
	margin-top: 20px;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

export const AuthActionPrimaryButton = styled(AuthPrimaryButton)`
	flex: 1;
	margin-top: 0;
	width: auto;
	min-width: 0;
	height: 46px;
	min-height: 46px;
	padding: 0 24px;
	border-radius: 10px;
	font-size: 16px;
	font-weight: 600;
	line-height: 1.4;
	letter-spacing: -0.48px;

	@media (max-width: 767px) {
		flex: 1 1 0;
		padding: 0 16px;
	}
`;

export const AuthDivider = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background: var(--bt-auth-tab-border, #2b3245);
	}
`;

export const AuthDividerText = styled.span`
	flex-shrink: 0;
	color: var(--bt-auth-paragraph-color, #a5b1c1);
	font-family: var(--bt-font-family, inherit);
	font-size: 12px;
	font-weight: 400;
	line-height: 1.4;
	letter-spacing: -0.36px;
	text-align: center;
`;

export const AuthPasskeyButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	padding: 10px 20px;
	border: 1px solid var(--bt-auth-social-border, #2d354c);
	border-radius: 9px;
	background: transparent;
	color: var(--bt-auth-heading-color, #e5f0fe);
	font-size: 14px;
	font-weight: 600;
	line-height: 1.4;
	text-transform: capitalize;
	cursor: pointer;

	svg {
		width: 16px;
		height: 16px;
	}
`;

export const AuthSocialRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
`;

export const AuthSocialButton = styled.button`
	display: flex;
	flex: 1 1 0;
	align-items: center;
	justify-content: center;
	min-width: 0;
	padding: 14px;
	border: 1px solid var(--bt-auth-social-border, #2d354c);
	border-radius: 12px;
	background: transparent;
	cursor: pointer;
	transition: border-color 0.15s ease;

	svg {
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}

	&:disabled {
		opacity: 1;
		cursor: not-allowed;
		border-color: var(--bt-auth-social-border, #2d354c);
		background: transparent;

		svg {
			opacity: 0.45;
		}
	}

	&:not(:disabled):hover {
		border-color: #3a4258;
	}
`;

export const AuthGoogleButtonHost = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
	min-height: 40px;
	border: 1px solid #2d354c;
	border-radius: 10px;
	overflow: hidden;

	iframe {
		margin: 0 !important;
	}
`;

export const AuthActionRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	margin-top: 16px;
	height: ${({ $single }) => ($single ? "auto" : "50px")};
	padding: ${({ $single }) => ($single ? "0" : "2px")};
	border-radius: 12px;
	background: ${({ $single }) => ($single ? "transparent" : "#272e43")};
	box-sizing: border-box;
`;

export const AuthSecondaryButton = styled.button`
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	justify-content: center;
	height: 46px;
	min-height: 46px;
	padding: 0 60px;
	border: none;
	border-radius: 10px;
	background: transparent;
	color: #e5f0fe;
	font-size: 16px;
	font-weight: 600;
	line-height: 1.4;
	letter-spacing: -0.48px;
	cursor: pointer;
	box-sizing: border-box;

	@media (max-width: 767px) {
		flex: 1 1 0;
		min-width: 0;
		padding: 0 16px;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

export const AuthHelpText = styled.p`
	margin: 12px 0 0;
	text-align: center;
	color: var(--bt-auth-paragraph-color, #a5b1c1);
	font-size: 12px;
	font-weight: 400;
	line-height: 1.4;
	letter-spacing: -0.36px;
`;

export const AuthFooterLink = styled.p`
	margin: 12px 0 0;
	text-align: center;
	color: #a5b1c1;
	font-size: 13px;
	font-weight: 400;
	line-height: 140%;

	button {
		border: none;
		background: transparent;
		padding: 0;
		color: #2c85f0;
		font-weight: 400;
		text-decoration: underline;
		cursor: pointer;
	}
`;

export const AuthStepContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
	overflow: visible;

	${(props) =>
		props.$scrollable &&
		css`
			max-height: min(58vh, 480px);
			overflow-y: auto;
			overflow-x: visible;
			padding: 3px 2px 4px 3px;
			box-sizing: border-box;

			@media (max-width: 1023px) {
				max-height: none;
				overflow-y: visible;
			}
		`}

	${(props) =>
		props.$expanded &&
		css`
			max-height: none;
		`}
`;

export const AuthStepsIndicator = styled.div`
	position: absolute;
	top: ${({ $wide }) => ($wide ? "36px" : "31px")};
	right: ${({ $wide }) => ($wide ? "58px" : "50px")};
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	color: #a5b1c1;
	font-size: 12px;
	font-weight: 400;
	line-height: 1;
`;

export const AuthCaptchaContainer = styled.div`
	padding: 30px;
	margin-top: -30px;
	background: #171c2a;
	border: 1px solid #212737;
	border-radius: 20px;
	display: none;
`;

export const AuthAlertIconWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	margin: 0 auto 12px;
	border-radius: 12px;
	background: var(--bt-auth-input-bg, #1f2536);
	border: 1px solid var(--bt-auth-tab-border, #2b3245);

	svg {
		width: 28px;
		height: 28px;
		flex-shrink: 0;
	}
`;

export const AuthAlertTitle = styled.h2`
	margin: 0;
	color: var(--bt-auth-heading-color, #e5f0fe);
	font-size: 24px;
	font-weight: 600;
	line-height: 1.2;
	text-align: center;
	letter-spacing: -0.72px;
`;

export const AuthAlertMessage = styled.div`
	margin-top: 8px;
	padding: 14px 16px;
	border-radius: 12px;
	text-align: center;
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
	color: var(--bt-auth-heading-color, #e5f0fe);
	background: ${({ $type }) =>
		$type === "error"
			? "rgba(239, 68, 68, 0.12)"
			: $type === "success"
				? "rgba(34, 197, 94, 0.12)"
				: "var(--bt-auth-input-bg, #1f2536)"};
	border: 1px solid
		${({ $type }) =>
			$type === "error"
				? "rgba(239, 68, 68, 0.2)"
				: $type === "success"
					? "rgba(34, 197, 94, 0.2)"
					: "var(--bt-auth-modal-border, #212737)"};
`;

export const AuthAlertSecondaryButton = styled(AuthSecondaryButton)`
	flex: 1 1 0;
	min-width: 0;
	width: auto;
	padding: 0 16px;
`;

const spin = keyframes`
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
`;

export const AuthButtonSpinner = styled.span`
	display: inline-block;
	width: 20px;
	height: 20px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-radius: 50%;
	border-top-color: #fff;
	animation: ${spin} 0.8s linear infinite;
`;
